/**
 * Selling Plan Picker Custom Element
 * Provides subscription vs one-time purchase selection for main-product template.
 *
 * Features:
 * - Stacked radio card UI for Subscribe & Save / One-Time toggle
 * - Inline price display with subscription discount
 * - Integration with product-info.js via pub/sub events
 * - Uses subscription-insurance-core.js for insurance handling
 */

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', class extends HTMLElement {
    constructor() {
      super();

      // Cache DOM elements
      this.purchaseOptions = this.querySelectorAll('[data-purchase-option]');
      this.subscribeOption = this.querySelector('[data-purchase-option="subscribe"]');
      this.onetimeOption = this.querySelector('[data-purchase-option="onetime"]');
      this.subscribePriceEl = this.querySelector('[data-subscribe-price]');
      this.onetimePriceEl = this.querySelector('[data-onetime-price]');
      this.savingsLineEl = this.querySelector('[data-savings-line]');
      this.subscribeCompareEl = this.querySelector('[data-subscribe-compare]');

      // Get configuration from data attributes
      this.sectionId = this.dataset.sectionId;
      this.sellingPlanId = this.dataset.sellingPlanId;
      this.discountPercent = parseInt(this.dataset.discountPercent, 10) || 0;
      this.frequency = parseInt(this.dataset.frequency, 10) || null;
      this.defaultOption = this.dataset.defaultOption || 'subscribe';

      // State
      this.isSubscribe = this.defaultOption === 'subscribe';
      this.currentVariantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
      this.compareAtPrice = parseInt(this.dataset.variantCompareAtPrice, 10) || 0;

      this.init();
    }

    init() {
      this.bindEvents();
      this.updateUI();
      this.updateFormInput();  // Set initial selling_plan value on page load
      this.updateMainPrice();   // Sync hero price on load
      this.updateStickyPrice(); // Sync sticky bar price on load
      this.updateVariantCardPrices(); // Sync every quantity card with the active purchase mode

      // Subscribe to variant changes from product-info.js
      if (typeof subscribe === 'function') {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
      }

      // Check cart for insurance state on load (in background)
      if (window.SubscriptionInsuranceCore) {
        window.SubscriptionInsuranceCore.checkCartForInsurance();
      }

      // Set up bidirectional sync with sticky cart dropdown
      this.setupStickySyncListeners();
    }

    bindEvents() {
      // Purchase option clicks (on the label cards)
      this.purchaseOptions.forEach(option => {
        option.addEventListener('click', (e) => {
          // Don't prevent default — let the radio input get checked naturally
          this.selectPurchaseType(option.dataset.purchaseOption);
        });

        option.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectPurchaseType(option.dataset.purchaseOption);
          }
        });
      });
    }

    /**
     * Handle purchase type selection
     */
    selectPurchaseType(type) {
      this.isSubscribe = type === 'subscribe';
      this.updateUI();
      this.publishSellingPlanChange();
      this.updateFormInput();
      this.updateStickyDropdown();
      this.updateMainPrice();
      this.updateStickyPrice();
      this.updateVariantCardPrices();
    }

    /**
     * Update visual state of radio cards
     */
    updateUI() {
      // Toggle active class on option labels + check radios
      this.purchaseOptions.forEach(option => {
        const isSelected = (option.dataset.purchaseOption === 'subscribe') === this.isSubscribe;
        option.classList.toggle('selling-plan-picker__option--active', isSelected);

        const radio = option.querySelector('input[type="radio"]');
        if (radio) radio.checked = isSelected;
      });

      // Update inline prices
      this.updatePriceDisplay();
    }

    /**
     * Update price displays based on current variant and selection
     */
    updatePriceDisplay() {
      if (!this.currentVariantPrice) return;

      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });

      const regularPrice = this.currentVariantPrice / 100;
      const subscriptionPrice = regularPrice * (1 - this.discountPercent / 100);

      if (this.subscribePriceEl) {
        this.subscribePriceEl.textContent = formatter.format(subscriptionPrice);
      }

      if (this.onetimePriceEl) {
        this.onetimePriceEl.textContent = formatter.format(regularPrice);
      }

      // Update compare price in subscribe row
      if (this.subscribeCompareEl) {
        this.subscribeCompareEl.textContent = formatter.format(regularPrice);
      }

      // Update savings line in subscribe detail
      if (this.savingsLineEl && this.discountPercent > 0) {
        const frequencyText = this.frequency ? `Delivery every ${this.frequency} days \u00b7 Cancel anytime` : 'Cancel anytime';
        this.savingsLineEl.textContent = '';
        this.savingsLineEl.appendChild(document.createTextNode(frequencyText));
        const convenienceSpan = document.createElement('span');
        convenienceSpan.className = 'selling-plan-picker__convenience';
        convenienceSpan.textContent = 'Never run out \u2014 skip the trip to the store.';
        this.savingsLineEl.appendChild(convenienceSpan);
      }
    }

    /**
     * Keep every ammunition quantity card aligned with the selected purchase
     * mode. Subscription prices come from each variant's selling-plan
     * allocation, so cases and boxes can have different discounted totals.
     */
    updateVariantCardPrices() {
      const productInfo = this.closest('product-info');
      const scope = productInfo || document;
      const cards = scope.querySelectorAll('[data-ammo-pack-card]');
      if (!cards.length) return;

      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });

      cards.forEach(card => {
        const onetimePrice = parseInt(card.dataset.onetimePriceCents, 10) || 0;
        const subscriptionPrice = parseInt(card.dataset.subscriptionPriceCents, 10) || onetimePrice;
        const compareAtPrice = parseInt(card.dataset.compareAtPriceCents, 10) || 0;
        const hasSubscription = card.dataset.subscriptionAvailable === 'true';
        const useSubscriptionPrice = this.isSubscribe && hasSubscription;
        const displayPrice = useSubscriptionPrice ? subscriptionPrice : onetimePrice;
        const effectiveComparePrice = useSubscriptionPrice
          ? Math.max(compareAtPrice, onetimePrice)
          : compareAtPrice;
        const savings = effectiveComparePrice > displayPrice
          ? effectiveComparePrice - displayPrice
          : 0;
        const discountPercent = savings > 0
          ? Math.round((savings * 100) / effectiveComparePrice)
          : 0;

        const currentPriceEl = card.querySelector('[data-ammo-pack-current-price]');
        const comparePriceEl = card.querySelector('[data-ammo-pack-compare-price]');
        const savingsEl = card.querySelector('[data-ammo-pack-savings]');
        const badgeEl = card.querySelector('[data-ammo-pack-badge]');

        if (currentPriceEl) {
          currentPriceEl.textContent = formatter.format(displayPrice / 100);
        }

        if (comparePriceEl) {
          comparePriceEl.hidden = savings <= 0;
          if (savings > 0) {
            comparePriceEl.textContent = formatter.format(effectiveComparePrice / 100);
          }
        }

        if (savingsEl) {
          savingsEl.hidden = savings <= 0;
          if (savings > 0) {
            savingsEl.textContent = `Save ${formatter.format(savings / 100)}`;
          }
        }

        if (badgeEl) {
          badgeEl.hidden = discountPercent <= 0;
          if (discountPercent > 0) {
            badgeEl.textContent = `Save ${discountPercent}%`;
          }
        }
      });
    }

    /**
     * Update the sticky bar price to reflect subscription/one-time selection,
     * including compare-at (strikethrough) display when subscribed.
     */
    updateStickyPrice() {
      if (!this.currentVariantPrice) return;

      const stickyPriceBlock = document.getElementById(`Price-Alt-${this.sectionId}`);
      if (!stickyPriceBlock) return;

      const priceDiv = stickyPriceBlock.querySelector('.price');
      if (!priceDiv) return;

      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });

      const regularPrice = this.currentVariantPrice / 100;

      if (this.isSubscribe && this.discountPercent > 0) {
        const subscriptionPrice = regularPrice * (1 - this.discountPercent / 100);

        // Switch to sale layout (shows .price__sale, hides .price__regular)
        priceDiv.classList.add('price--on-sale');

        // Update compare-at (strikethrough) price — show MSRP if available
        const compareEl = priceDiv.querySelector('.price__sale s price-money bdi');
        if (compareEl) {
          const strikethroughPrice = this.compareAtPrice > 0 ? this.compareAtPrice / 100 : regularPrice;
          compareEl.textContent = formatter.format(strikethroughPrice);
        }

        // Update sale price
        const saleEl = priceDiv.querySelector('.price__sale .price-item--sale price-money bdi');
        if (saleEl) {
          saleEl.textContent = formatter.format(subscriptionPrice);
        }
      } else if (this.compareAtPrice > this.currentVariantPrice) {
        // One-time but product is on sale — keep strikethrough from MSRP
        priceDiv.classList.add('price--on-sale');

        const compareEl = priceDiv.querySelector('.price__sale s price-money bdi');
        if (compareEl) {
          compareEl.textContent = formatter.format(this.compareAtPrice / 100);
        }

        const saleEl = priceDiv.querySelector('.price__sale .price-item--sale price-money bdi');
        if (saleEl) {
          saleEl.textContent = formatter.format(regularPrice);
        }
      } else {
        // No discount — plain regular layout
        priceDiv.classList.remove('price--on-sale');

        const regularEl = priceDiv.querySelector('.price__regular .price-item--regular price-money bdi');
        if (regularEl) {
          regularEl.textContent = formatter.format(regularPrice);
        }
      }
    }

    /**
     * Update the main price block to reflect subscription/one-time selection,
     * including compare-at (strikethrough) display and sale badge when subscribed.
     */
    updateMainPrice() {
      if (!this.currentVariantPrice) return;

      const priceBlock = document.getElementById(`price-${this.sectionId}`);
      if (!priceBlock) return;

      const priceDiv = priceBlock.querySelector('.price');
      if (!priceDiv) return;

      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });

      const regularPrice = this.currentVariantPrice / 100;
      const badge = priceDiv.querySelector('.price__badge-sale');

      if (this.isSubscribe && this.discountPercent > 0) {
        const subscriptionPrice = regularPrice * (1 - this.discountPercent / 100);

        // Switch to sale layout (shows .price__sale, hides .price__regular)
        priceDiv.classList.add('price--on-sale');

        // Update compare-at (strikethrough) price — show MSRP if available
        const compareEl = priceDiv.querySelector('.price__sale s price-money bdi');
        if (compareEl) {
          const strikethroughPrice = this.compareAtPrice > 0 ? this.compareAtPrice / 100 : regularPrice;
          compareEl.textContent = formatter.format(strikethroughPrice);
        }

        // Update sale price
        const saleEl = priceDiv.querySelector('.price__sale .price-item--sale price-money bdi');
        if (saleEl) {
          saleEl.textContent = formatter.format(subscriptionPrice);
        }

        // Update badge to show total savings from compare_at_price
        if (badge && this.compareAtPrice > 0) {
          const subscriptionPriceCents = Math.round(subscriptionPrice * 100);
          const totalDiscount = Math.round(
            ((this.compareAtPrice - subscriptionPriceCents) * 100) / this.compareAtPrice
          );
          badge.textContent = `\u2013${totalDiscount}%`;
          badge.style.display = '';
        }
      } else if (this.compareAtPrice > this.currentVariantPrice) {
        // One-time but product is already on sale (compare_at_price > price)
        // Keep sale layout to show strikethrough from compare_at_price
        priceDiv.classList.add('price--on-sale');

        const compareAtDollars = this.compareAtPrice / 100;

        // Update compare-at (strikethrough) to original MSRP
        const compareEl = priceDiv.querySelector('.price__sale s price-money bdi');
        if (compareEl) {
          compareEl.textContent = formatter.format(compareAtDollars);
        }

        // Update sale price to variant price
        const saleEl = priceDiv.querySelector('.price__sale .price-item--sale price-money bdi');
        if (saleEl) {
          saleEl.textContent = formatter.format(regularPrice);
        }

        // Restore badge to compare_at_price vs variant price
        if (badge) {
          const discount = Math.round((this.compareAtPrice - this.currentVariantPrice) / this.compareAtPrice * 100);
          badge.textContent = `\u2013${discount}%`;
          badge.style.display = '';
        }
      } else {
        // One-time, no compare_at_price discount — plain regular layout
        priceDiv.classList.remove('price--on-sale');

        const regularEl = priceDiv.querySelector('.price__regular .price-item--regular price-money bdi');
        if (regularEl) {
          regularEl.textContent = formatter.format(regularPrice);
        }

        if (badge) badge.style.display = 'none';
      }
    }

    /**
     * Handle variant change from product-info.js
     */
    onVariantChange({ data }) {
      if (!data?.variant) return;

      this.currentVariantPrice = data.variant.price;
      this.compareAtPrice = data.variant.compare_at_price || 0;
      this.updatePriceDisplay();

      // Update selling plan ID if variant has different plans
      if (data.variant.selling_plan_allocations?.length > 0) {
        const allocation = data.variant.selling_plan_allocations[0];
        this.sellingPlanId = allocation.selling_plan_id;

        // Update discount percent from allocation
        if (allocation.price_adjustments?.length > 0) {
          const adjustment = allocation.price_adjustments[0];
          if (adjustment.value_type === 'percentage') {
            this.discountPercent = adjustment.value;
          }
        }
      }

      this.updatePriceDisplay();
      this.updateFormInput();
      this.updateMainPrice();
      this.updateStickyPrice();
      this.updateVariantCardPrices();
    }

    /**
     * Publish selling plan change event for other components
     */
    publishSellingPlanChange() {
      if (typeof publish === 'function') {
        publish('selling-plan-change', {
          data: {
            isSubscribe: this.isSubscribe,
            sellingPlanId: this.isSubscribe ? this.sellingPlanId : null,
            discountPercent: this.isSubscribe ? this.discountPercent : 0,
            frequency: this.isSubscribe ? this.frequency : null
          }
        });
      }

      // Also dispatch a DOM event for non-pub/sub listeners
      this.dispatchEvent(new CustomEvent('selling-plan-change', {
        bubbles: true,
        detail: {
          isSubscribe: this.isSubscribe,
          sellingPlanId: this.isSubscribe ? this.sellingPlanId : null,
          frequency: this.isSubscribe ? this.frequency : null
        }
      }));
    }

    /**
     * Update the hidden selling_plan input in ALL product forms (main + sticky cart)
     */
    updateFormInput() {
      // Find ALL product forms (main form + sticky cart form)
      const productInfo = this.closest('product-info');
      const forms = productInfo
        ? productInfo.querySelectorAll('form[data-type="add-to-cart-form"]')
        : document.querySelectorAll('form[data-type="add-to-cart-form"]');

      if (!forms.length) return;

      const sellingPlanValue = this.isSubscribe ? (this.sellingPlanId || '') : '';

      // Update selling_plan input in ALL forms
      forms.forEach(form => {
        let sellingPlanInput = form.querySelector('input[name="selling_plan"]');

        if (!sellingPlanInput) {
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          form.appendChild(sellingPlanInput);
        }

        sellingPlanInput.value = sellingPlanValue;
      });
    }

    /**
     * Set up bidirectional sync with sticky cart selling plan dropdown
     */
    setupStickySyncListeners() {
      this.stickySelect = document.querySelector('.sticky-atc__selling-plan-select');
      if (!this.stickySelect) return;

      // Listen for sticky dropdown changes
      this.stickySelect.addEventListener('change', (e) => {
        const isSubscribe = e.target.value === 'subscribe';
        this.isSubscribe = isSubscribe;
        this.updateUI();
        this.updateFormInput();
        this.publishSellingPlanChange();
        this.updateMainPrice();
        this.updateStickyPrice();
        this.updateVariantCardPrices();
      });

      // Set initial state to match main picker
      this.updateStickyDropdown();
    }

    /**
     * Update the sticky dropdown to match current selection
     */
    updateStickyDropdown() {
      if (!this.stickySelect) {
        this.stickySelect = document.querySelector('.sticky-atc__selling-plan-select');
      }
      if (this.stickySelect) {
        this.stickySelect.value = this.isSubscribe ? 'subscribe' : 'onetime';
      }
    }

    /**
     * Get current state for form submission
     */
    getState() {
      return {
        isSubscribe: this.isSubscribe,
        sellingPlanId: this.isSubscribe ? this.sellingPlanId : null,
        frequency: this.isSubscribe ? this.frequency : null,
        discountPercent: this.isSubscribe ? this.discountPercent : 0
      };
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }
  });
}

/**
 * Enhanced Product Form Handler
 * Intercepts form submission to handle subscription insurance
 */
document.addEventListener('DOMContentLoaded', () => {
  // Find all product forms that have a selling-plan-picker
  const pickers = document.querySelectorAll('selling-plan-picker');

  pickers.forEach(picker => {
    const productInfo = picker.closest('product-info');
    const productForm = productInfo?.querySelector('product-form');

    if (!productForm) return;

    const form = productForm.querySelector('form');
    if (!form) return;

    // Store original submit handler reference
    const originalOnSubmit = productForm.onSubmitHandler?.bind(productForm);

    // Override form submission to handle insurance
    form.addEventListener('submit', async (e) => {
      const state = picker.getState();

      // If not a subscription, let normal flow handle it
      // (shipping-insurance.js will handle one-time insurance)
      if (!state.isSubscribe) {
        return;
      }

      // For subscriptions, we need to handle insurance with shortest tenor logic
      if (!window.SubscriptionInsuranceCore) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.classList.add('loading');
        submitButton.setAttribute('disabled', 'disabled');
      }

      try {
        // Determine insurance action
        const insuranceAction = await window.SubscriptionInsuranceCore.determineInsuranceAction(
          true,
          state.frequency
        );

        // Handle swap if needed (by current keys: a discount code in the cart re-keys lines, #PA52681)
        if (insuranceAction.action === 'swap') {
          await window.SubscriptionInsuranceCore.removeReplacedInsurance(null);
        }

        // Build cart items
        const items = [];

        // Main product
        const variantInput = form.querySelector('input[name="id"]');
        const quantityInput = form.querySelector('input[name="quantity"]');

        // Parse selling plan ID (handles both numeric and GID formats)
        const parsedSellingPlan = parseInt(state.sellingPlanId, 10);
        const sellingPlanValue = !isNaN(parsedSellingPlan) ? parsedSellingPlan : state.sellingPlanId;

        items.push({
          id: parseInt(variantInput.value, 10),
          quantity: parseInt(quantityInput?.value || '1', 10),
          selling_plan: sellingPlanValue
        });

        // Insurance if needed
        if (insuranceAction.action === 'add' || insuranceAction.action === 'swap') {
          const insuranceItem = window.SubscriptionInsuranceCore.buildInsuranceItem(
            insuranceAction.addType,
            insuranceAction.frequency
          );
          if (insuranceItem) items.push(insuranceItem);
        }

        // Add to cart
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();

        if (response.ok) {
          // Success - trigger cart update
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'selling-plan-picker',
              productVariantId: variantInput.value,
              cartData: result
            });
          }

          // Update mini cart if available
          const miniCart = document.querySelector('mini-cart');
          if (miniCart?.renderContents) {
            // Fetch updated sections
            const sectionsResponse = await fetch(
              `${window.Shopify.routes.root}?sections=mini-cart,cart-icon-bubble`
            );
            const sections = await sectionsResponse.json();
            miniCart.renderContents({ sections });
          }

          // Dispatch success event
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: result },
            bubbles: true
          }));

        } else {
          // Error
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'selling-plan-picker',
              productVariantId: variantInput.value,
              errors: result.errors || result.description
            });
          }

          // Show error in product form
          const errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
          const errorMessage = productForm.querySelector('.product-form__error-message');
          if (errorWrapper && errorMessage) {
            errorMessage.textContent = result.description || 'Could not add to cart';
            errorWrapper.hidden = false;
          }
        }

      } catch (error) {
        console.error('[SellingPlanPicker] Error adding to cart:', error);
      } finally {
        // Clear cart cache after operation
        window.SubscriptionInsuranceCore?.clearCartCache?.();

        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('disabled');
        }
      }
    }, { capture: true });

    // Intercept Buy It Now for BOTH subscription and one-time purchases
    // Shopify's PaymentButton SDK doesn't work reliably with selling plan products,
    // so we handle both cases ourselves for consistent behavior and automatic insurance
    const paymentButtonContainer = productInfo?.querySelector('.shopify-payment-button');

    if (paymentButtonContainer) {
      paymentButtonContainer.addEventListener('click', async (e) => {
        const state = picker.getState();

        // Always intercept - handle both subscription and one-time
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const buyNowButton = paymentButtonContainer.querySelector('button');
        if (buyNowButton) buyNowButton.disabled = true;

        try {
          // Build cart items
          const items = [];
          const variantInput = form.querySelector('input[name="id"]');
          const quantityInput = form.querySelector('input[name="quantity"]');

          const mainItem = {
            id: parseInt(variantInput.value, 10),
            quantity: parseInt(quantityInput?.value || '1', 10)
          };

          // Add selling plan only for subscriptions
          if (state.isSubscribe && state.sellingPlanId) {
            const parsedPlan = parseInt(state.sellingPlanId, 10);
            mainItem.selling_plan = !isNaN(parsedPlan) ? parsedPlan : state.sellingPlanId;
          }

          items.push(mainItem);

          // Handle insurance if core module is available
          if (window.SubscriptionInsuranceCore) {
            const insuranceAction = await window.SubscriptionInsuranceCore.determineInsuranceAction(
              state.isSubscribe,
              state.isSubscribe ? state.frequency : null
            );

            // Handle swap if needed (by current keys: a discount code in the cart re-keys lines, #PA52681)
            if (insuranceAction.action === 'swap') {
              await window.SubscriptionInsuranceCore.removeReplacedInsurance(null);
            }

            // Add insurance if needed
            if (insuranceAction.action === 'add' || insuranceAction.action === 'swap') {
              const insuranceItem = window.SubscriptionInsuranceCore.buildInsuranceItem(
                insuranceAction.addType,
                insuranceAction.frequency
              );
              if (insuranceItem) items.push(insuranceItem);
            }
          }

          // Add to cart
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items })
          });

          if (response.ok) {
            window.location.href = '/checkout';
          } else {
            const result = await response.json();
            console.error('[SellingPlanPicker] Buy It Now - Cart error:', result);
            alert(result.description || 'Could not add to cart');
          }

        } catch (error) {
          console.error('[SellingPlanPicker] Buy It Now - Error:', error);
        } finally {
          window.SubscriptionInsuranceCore?.clearCartCache?.();
          if (buyNowButton) buyNowButton.disabled = false;
        }
      }, { capture: true });
    }
  });
});
